import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { Trans } from "@lingui/react/macro"
import { Alert, Anchor, Button, Center, Loader, PasswordInput, Stack, TextInput } from "@mantine/core"
import { useCallback, useState } from "react"
import { AuthShell } from "../../-auth-shared/AuthShell"
import type { IProps } from "./interfaces"

export const InviteView = ({
  logo,
  appName,
  product,
  loginBackground,
  state,
  organizationName,
  email,
  error,
  loading,
  loginHref,
  onSubmit,
}: IProps): React.JSX.Element => {
  const [name, setName] = useState("")
  const [password, setPassword] = useState("")
  const [confirm, setConfirm] = useState("")
  const { i18n } = useLingui()

  const mismatch = confirm.length > 0 && password !== confirm

  const handleSubmit = useCallback(
    (e: React.FormEvent): void => {
      e.preventDefault()
      if (password !== confirm) return
      onSubmit(name, password)
    },
    [name, password, confirm, onSubmit],
  )

  if (state === "loading") {
    return (
      <AuthShell
        logo={logo}
        appName={appName}
        product={product}
        loginBackground={loginBackground}
        title={<Trans>Checking your invitation…</Trans>}
      >
        <Center py="md">
          <Loader />
        </Center>
      </AuthShell>
    )
  }

  if (state === "invalid") {
    return (
      <AuthShell
        logo={logo}
        appName={appName}
        product={product}
        loginBackground={loginBackground}
        title={<Trans>Invitation not found</Trans>}
      >
        <Stack>
          <Alert color="danger" variant="light">
            <Trans>This invitation is invalid, has expired, or was already used.</Trans>
          </Alert>
          <Anchor href="/login" ta="center" size="sm">
            <Trans>Go to sign in</Trans>
          </Anchor>
        </Stack>
      </AuthShell>
    )
  }

  if (state === "needs-login") {
    return (
      <AuthShell
        logo={logo}
        appName={appName}
        title={<Trans>Join {organizationName}</Trans>}
        subtitle={<Trans>You already have an account — sign in to accept this invitation.</Trans>}
      >
        <Button component="a" href={loginHref ?? "/login"} fullWidth>
          <Trans>Sign in to continue</Trans>
        </Button>
      </AuthShell>
    )
  }

  if (state === "success") {
    return (
      <AuthShell
        logo={logo}
        appName={appName}
        product={product}
        loginBackground={loginBackground}
        title={<Trans>Welcome aboard!</Trans>}
      >
        <Center py="md">
          <Loader />
        </Center>
      </AuthShell>
    )
  }

  return (
    <AuthShell
      logo={logo}
      appName={appName}
      title={<Trans>Join {organizationName}</Trans>}
      subtitle={email ? <Trans>Setting up {email}</Trans> : undefined}
    >
      <form onSubmit={handleSubmit}>
        <Stack>
          {error ? (
            <Alert color="danger" variant="light">
              {error}
            </Alert>
          ) : null}
          <TextInput
            label={i18n._(msg`Your name`)}
            value={name}
            onChange={(e) => setName(e.currentTarget.value)}
            required
            autoFocus
          />
          <PasswordInput
            label={i18n._(msg`Create a password`)}
            placeholder={i18n._(msg`At least 8 characters`)}
            value={password}
            onChange={(e) => setPassword(e.currentTarget.value)}
            required
          />
          <PasswordInput
            label={i18n._(msg`Confirm password`)}
            value={confirm}
            onChange={(e) => setConfirm(e.currentTarget.value)}
            error={mismatch ? i18n._(msg`Passwords don't match`) : undefined}
            required
          />
          <Button type="submit" fullWidth loading={loading} disabled={password.length < 8 || mismatch || !name}>
            <Trans>Accept & sign in</Trans>
          </Button>
        </Stack>
      </form>
    </AuthShell>
  )
}
